import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import * as FaIcons from "react-icons/fa";
import { TfiNewWindow } from "react-icons/tfi";
import { Tooltip } from "@material-tailwind/react";

import ScrollContainer, { SCROLLBAR_THEME } from "src/components/core/ScrollContainer";
import logo from "src/assets/Landvoice Logo 2.png";
import useNavigation from "src/hooks/useNavigation";
import { SIDEBAR } from "src/constants/zIndexes";
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from "src/constants/ui";
import { EXTERNAL_URLS } from "src/constants/support";
import logoutIcon from "src/assets/right-from-bracket-solid.svg";

import { useAuth } from "src/components/utils/auth/assist/authContext";
import { addOns, coreProducts, filterByFeatureFlag } from "./data";
import { IconWrapper } from "./IconWrapper";
import ToggleSidebarButton from "src/components/common/ToggleSidebarButton";

interface SidebarMenuItemProps {
  to?: string;
  label: string;
  icon?: React.ReactNode;
  window?: boolean;
  onClick?: () => void;
  suppressTooltip: boolean;
  tooltip: string;
  showNewWindowIcon?: boolean;
  activePattern?: string;
}

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  to,
  label,
  icon,
  window,
  onClick,
  suppressTooltip,
  tooltip,
  showNewWindowIcon,
  activePattern,
}) => {
  const location = useLocation();
  const { isDesktopSidebarExpanded } = useNavigation();

  const content = (
    <Tooltip
      className={suppressTooltip ? "" : "sidebar-tooltip"}
      content={suppressTooltip ? "" : tooltip}
      placement="right"
    >
      <div
        className={`nav-button group fill-white ${
          !suppressTooltip ? "!w-auto" : "!w-[190px]"
        }`}
      >
        <IconWrapper>
          {icon || (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              className=""
            >
              <path d="M10.8 15.5288V15.6H9.6C8.93625 15.6 8.4 16.1363 8.4 16.8V20.1C8.4 20.9288 7.72875 21.6 6.9 21.6H4.80375C4.7475 21.6 4.69125 21.5963 4.635 21.5925C4.59 21.5963 4.545 21.6 4.5 21.6H3.9C3.07163 21.6 2.4 20.9288 2.4 20.1V15.9C2.4 15.8663 2.40112 15.8288 2.40337 15.795V13.185H1.20187C0.52575 13.185 0 12.6563 0 11.9813C0 11.6438 0.11265 11.3438 0.375375 11.0813L9.99 2.70062C10.2525 2.4376 10.5525 2.40002 10.815 2.40002C11.0775 2.40002 11.3775 2.47517 11.6063 2.66305L16.7738 7.20377C16.3088 7.17002 15.8363 7.31627 15.4688 7.64627C14.1413 8.83127 13.0088 10.185 12.195 11.5013C11.4113 12.7688 10.8 14.2088 10.8 15.5288ZM19.0875 10.7063C19.3838 10.335 19.7175 9.93003 20.0813 9.56628C20.2913 9.40877 20.6213 9.40877 20.8313 9.60002C21.7575 10.4513 22.5413 11.5763 23.0925 12.645C23.64 13.695 24 14.7713 24 15.5288C24 18.8213 21.3263 21.6 18 21.6C14.6363 21.6 12 18.8175 12 15.5288C12 14.5388 12.4763 13.3313 13.215 12.1313C13.965 10.92 15.0188 9.65252 16.2675 8.53877C16.4775 8.35127 16.7663 8.35502 17.0063 8.54252C17.7488 9.21002 18.4425 9.92627 19.0875 10.7063ZM20.625 15.0038C20.55 14.8538 20.475 14.7038 20.3625 14.5538L19.0125 16.1625C19.0125 16.1625 16.8375 13.3875 16.6875 13.2C15.5625 14.5538 15 15.375 15 16.275C15 18.1125 16.35 19.2 18.0375 19.2C18.7125 19.2 19.3125 19.0125 19.875 18.6038C21 17.85 21.3 16.275 20.625 15.0038Z" />
            </svg>
          )}
        </IconWrapper>
        <span className={!suppressTooltip ? "fade-out" : ""}>{label}</span>
        {showNewWindowIcon && window && isDesktopSidebarExpanded && (
          <span>
            <TfiNewWindow />
          </span>
        )}
      </div>
    </Tooltip>
  );

  return to ? (
    <NavLink
      to={to}
      className={({ isActive }) => {
        const customActive = activePattern ? location.pathname.startsWith(activePattern) : false;
        return (isActive || customActive) ? "nav-text active" : "nav-text";
      }}
      target={window ? "_blank" : ""}
    >
      {content}
    </NavLink>
  ) : (
    <div onClick={onClick} className={"nav-text"}>
      {content}
    </div>
  );
};

interface DesktopSidebarProps {
  showNewWindowIcon?: boolean;
}

const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  showNewWindowIcon = false
}) => {
  const { logout, user } = useAuth();
  const { isDesktopSidebarExpanded, toggleDesktopSidebar } = useNavigation();

  return (
    <>
      <ToggleSidebarButton
        onClick={() => toggleDesktopSidebar()}
        label={isDesktopSidebarExpanded ? "Close sidebar" : "Open sidebar"}
        position="left"
        direction={isDesktopSidebarExpanded ? "left" : "right"}
        style={{
          left: `${(isDesktopSidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH)}px`
        }}
      />
      <nav
        className={`sidebar-navigation ${`w-[${
          isDesktopSidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH
        }px]`} h-screen supports-[height:100dvh]:h-dvh hidden md:flex fixed top-0 left-0 bg-gray-900 flex-col justify-between p-[24px] pb-[max(24px,env(safe-area-inset-bottom))] transition-all duration-100 print:hidden`}
        style={{
          zIndex: SIDEBAR,
        }}
      >
        {/* Top section: Logo + scrollable menu */}
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <Link
            to="/"
            className="sidebar-logo flex items-center justify-center max-h-[64px] h-full text-white tracking-widest gap-[5px] print:hidden cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
          >
            <div className=" max-h-[24px]">
              <img height={"24px"} width={"24px"} src={logo} alt="" />{" "}
            </div>
            <p className={!isDesktopSidebarExpanded ? "fade-out" : ""}>
              LAND<span>VOICE</span>
            </p>
          </Link>
          <div className="flex-1 min-h-0 overflow-hidden print:hidden">
            <ScrollContainer theme={SCROLLBAR_THEME.light} className="h-full">
              <div className="flex flex-col mr-[10px]">
                {filterByFeatureFlag(coreProducts).map((page, index) =>
                  !(
                    user?.expired_upload === false &&
                    page.title === "Expired Uploads"
                  ) ? (
                    <SidebarMenuItem
                      key={`sidebar-menu-item-core-${index}`}
                      to={page.path}
                      window={page.window}
                      tooltip={page.title}
                      label={page.title}
                      icon={page.icon}
                      suppressTooltip={isDesktopSidebarExpanded}
                      showNewWindowIcon={showNewWindowIcon}
                      activePattern={page.activePattern}
                    />
                  ) : null
                )}
              </div>
              <div
                className={
                  isDesktopSidebarExpanded
                    ? " border-b border-gray-300 text-gray-secondary text-sm p-1 mr-4 mb-2 mt-5 border-gray-secondary"
                    : "fade-out"
                }
              >
                Add Ons
              </div>
              <div className="flex flex-col mr-[10px]">
                {addOns.map((page, index) => (
                  <SidebarMenuItem
                    key={`sidebar-menu-item-addons-${index}`}
                    to={page.path}
                    window={page.window}
                    tooltip={page.title}
                    label={page.title}
                    icon={page.icon}
                    suppressTooltip={isDesktopSidebarExpanded}
                    showNewWindowIcon={showNewWindowIcon}
                  />
                ))}
              </div>
            </ScrollContainer>
          </div>
        </div>

        {/* Bottom section: Help, Settings, Logout - always visible */}
        <div className="mr-[10px] flex-shrink-0 print:hidden">
            <SidebarMenuItem
              key="sidebar-menu-item-account-help"
              to={EXTERNAL_URLS.HELP_CENTER}
              tooltip="Help"
              label="Help"
              window
              icon={<FaIcons.FaQuestion />}
              suppressTooltip={isDesktopSidebarExpanded}
              showNewWindowIcon={showNewWindowIcon}
            />
            <SidebarMenuItem
              key="sidebar-menu-item-account-settings"
              to="/accountsettings"
              tooltip="Account Settings"
              label="Account Settings"
              icon={<FaIcons.FaCog />}
              suppressTooltip={isDesktopSidebarExpanded}
              showNewWindowIcon={showNewWindowIcon}
            />
            <SidebarMenuItem
              key="sidebar-menu-item-account-logout"
              tooltip="Log Out"
              label="Log Out"
              onClick={logout}
              icon={<img src={logoutIcon} alt="logout" style={{ width: "19px", height: "19px" }} />}
              suppressTooltip={isDesktopSidebarExpanded}
              showNewWindowIcon={showNewWindowIcon}
            />
        </div>
      </nav>
    </>
  );
};

export default DesktopSidebar;